// src/components/POI/ActivityFeedbackForm.jsx
// Form modal per raccogliere segnalazioni e miglioramenti su un'attività.
// Supporta tre tipi di azione: conferma, modifica, aggiunta.
// Salva i dati via POST /api/activity-feedback (senza toccare OSM direttamente).

import React, { useState } from 'react';
import { createPortal } from 'react-dom';

const API_BASE = (process.env.REACT_APP_API_URL_NODE || 'http://localhost:3002').replace(/\/$/, '');

const SERVIZI_OPTIONS = [
    'Pernottamento',
    'Ristorante',
    'Bar',
    'Docce calde',
    'WiFi',
    'Acqua potabile',
    'Ricarica telefoni',
    'Parcheggio',
    'Area Picnic',
    'Accessibile con animali',
];

/* ── Stili condivisi ─────────────────────────────────────────── */
const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 13,
    boxSizing: 'border-box',
};

const labelStyle = {
    display: 'block',
    fontSize: 12,
    color: '#555',
    marginBottom: 4,
};

const fieldWrap = { marginBottom: 12 };

/* ── Componente principale ───────────────────────────────────── */
export default function ActivityFeedbackForm({ poi, onClose }) {
    const props = poi?.properties || {};
    const coords = poi?.geometry?.coordinates;

    // Stato del form
    const [action, setAction] = useState('confirm');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState(props.phone || props['contact:phone'] || '');
    const [website, setWebsite] = useState(props.website || props['contact:website'] || '');
    const [servizi, setServizi] = useState([]);
    const [description, setDescription] = useState(action === 'modify' ? (props.description || '') : '');
    const [notes, setNotes] = useState('');
    const [isOwner, setIsOwner] = useState(false);

    // Stato UI
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const toggleServizio = (s) =>
        setServizi(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

    const handleActionChange = (val) => {
        setAction(val);
        // Precompila descrizione solo in modalità modifica
        if (val === 'modify') {
            setDescription(props.description || '');
            setPhone(props.phone || props['contact:phone'] || '');
            setWebsite(props.website || props['contact:website'] || '');
        } else if (val === 'add') {
            setDescription('');
            setPhone('');
            setWebsite('');
        }
        setServizi([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setError('L\'email è obbligatoria.');
            return;
        }
        setLoading(true);
        setError('');

        try {
            const payload = {
                activityId: poi?.id || props?.id || null,
                activityName: props.name || null,
                lat: coords?.[1] ?? null,
                lng: coords?.[0] ?? null,
                action,
                email: email.trim(),
                changes: (action === 'modify' || action === 'add')
                    ? { phone: phone.trim(), website: website.trim(), servizi, description: description.trim() }
                    : null,
                notes: notes.trim() || null,
                isOwner: action === 'confirm' ? isOwner : false,
            };

            const res = await fetch(`${API_BASE}/api/activity-feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Errore HTTP ${res.status}`);
            }

            setSubmitted(true);
        } catch (err) {
            setError(`Errore durante l'invio. Riprova o scrivi a info@aihiking.eu`);
            console.error('[ActivityFeedbackForm]', err);
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.8)', zIndex: 5000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: 'white', borderRadius: 12, padding: 24,
                    maxWidth: 480, width: '100%', maxHeight: '90vh', overflow: 'auto',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    marginBottom: 16, borderBottom: '3px solid #2196F3', paddingBottom: 12,
                }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 20, color: '#333' }}>📩 Migliora queste informazioni</h2>
                        {props.name && (
                            <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#666' }}>{props.name}</p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#666', padding: 0, lineHeight: 1 }}
                    >
                        ×
                    </button>
                </div>

                {/* ── Stato: successo ── */}
                {submitted ? (
                    <div style={{ textAlign: 'center', padding: '30px 20px' }}>
                        <p style={{ fontSize: 48, margin: '0 0 16px 0' }}>✅</p>
                        <h3 style={{ color: '#4CAF50', margin: '0 0 8px 0', fontSize: 20 }}>Grazie!</h3>
                        <p style={{ color: '#555', fontSize: 14, lineHeight: 1.5 }}>
                            Verificheremo le informazioni e le aggiungeremo anche su OpenStreetMap.
                        </p>
                        <button
                            onClick={onClose}
                            style={{
                                marginTop: 20, padding: '10px 28px',
                                background: '#4CAF50', color: 'white',
                                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14,
                            }}
                        >
                            Chiudi
                        </button>
                    </div>
                ) : (
                    /* ── Form ── */
                    <form onSubmit={handleSubmit}>

                        {/* Step 1 – Tipo segnalazione */}
                        <div style={{ marginBottom: 18 }}>
                            <p style={{ margin: '0 0 10px 0', fontWeight: 600, fontSize: 13, color: '#333' }}>
                                Tipo di segnalazione
                            </p>
                            {[
                                { value: 'confirm', label: '✅ Confermo che i dati sono corretti' },
                                { value: 'modify', label: '✏️ Voglio modificare alcune informazioni' },
                                { value: 'add', label: '➕ Voglio aggiungere informazioni' },
                            ].map(opt => (
                                <label
                                    key={opt.value}
                                    style={{ display: 'block', marginBottom: 8, cursor: 'pointer', fontSize: 13 }}
                                >
                                    <input
                                        type="radio"
                                        value={opt.value}
                                        checked={action === opt.value}
                                        onChange={() => handleActionChange(opt.value)}
                                        style={{ marginRight: 8 }}
                                    />
                                    {opt.label}
                                </label>
                            ))}
                        </div>

                        {/* Email – sempre obbligatoria */}
                        <div style={fieldWrap}>
                            <label style={labelStyle}>
                                Email <span style={{ color: '#f44336' }}>*</span>
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="la-tua@email.it"
                                required
                                style={inputStyle}
                            />
                        </div>

                        {/* Nome attività – readonly */}
                        <div style={fieldWrap}>
                            <label style={labelStyle}>Attività</label>
                            <input
                                type="text"
                                value={props.name || ''}
                                readOnly
                                style={{ ...inputStyle, background: '#f5f5f5', color: '#888' }}
                            />
                        </div>

                        {/* Campi condizionali: modifica / aggiunta */}
                        {(action === 'modify' || action === 'add') && (
                            <>
                                <div style={fieldWrap}>
                                    <label style={labelStyle}>Telefono</label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        placeholder="+39 0974 ..."
                                        style={inputStyle}
                                    />
                                </div>

                                <div style={fieldWrap}>
                                    <label style={labelStyle}>Sito web</label>
                                    <input
                                        type="url"
                                        value={website}
                                        onChange={e => setWebsite(e.target.value)}
                                        placeholder="https://..."
                                        style={inputStyle}
                                    />
                                </div>

                                <div style={fieldWrap}>
                                    <label style={{ ...labelStyle, marginBottom: 8 }}>Servizi disponibili</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
                                        {SERVIZI_OPTIONS.map(s => (
                                            <label
                                                key={s}
                                                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={servizi.includes(s)}
                                                    onChange={() => toggleServizio(s)}
                                                />
                                                {s}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div style={fieldWrap}>
                                    <label style={labelStyle}>Descrizione</label>
                                    <textarea
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        rows={3}
                                        placeholder="Descrivi brevemente l'attività..."
                                        style={{ ...inputStyle, resize: 'vertical' }}
                                    />
                                </div>
                            </>
                        )}

                        {/* Conferma: checkbox proprietario */}
                        {action === 'confirm' && (
                            <div style={fieldWrap}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={isOwner}
                                        onChange={e => setIsOwner(e.target.checked)}
                                    />
                                    Sono il proprietario o gestore di questa attività
                                </label>
                            </div>
                        )}

                        {/* Note – sempre presenti */}
                        <div style={fieldWrap}>
                            <label style={labelStyle}>Note aggiuntive</label>
                            <textarea
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                rows={2}
                                placeholder="Qualsiasi altra informazione utile..."
                                style={{ ...inputStyle, resize: 'vertical' }}
                            />
                        </div>

                        {error && (
                            <p style={{ color: '#f44336', fontSize: 12, margin: '0 0 12px 0' }}>{error}</p>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: '100%', padding: '12px',
                                background: loading ? '#ccc' : '#2196F3',
                                color: 'white', border: 'none', borderRadius: 8,
                                fontSize: 15, fontWeight: 600,
                                cursor: loading ? 'default' : 'pointer',
                            }}
                        >
                            {loading ? 'Invio in corso...' : '📩 Invia segnalazione'}
                        </button>

                        <p style={{ textAlign: 'center', fontSize: 11, color: '#999', margin: '8px 0 0 0' }}>
                            Nessuna registrazione richiesta
                        </p>
                    </form>
                )}
            </div>
        </div>,
        document.body
    );
}
